import React from 'react'
import { Box, Button, Chip, Typography } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { formatLocalDateTime } from './dateFormatting'
import { goBackTarget } from './goBackTarget'

/**
 * Historia 176 — B5, RN16b, `03-dominio.md` §6.1 "Entrada del histórico". ONE entry of
 * `GET …/terms/history` (`TermChangeDto`), identical in the ⏱ history of a row and in
 * "Language history": previous → new (T7 when null), author, date, restoration and the restore button.
 *
 * @param {{entry: object, entries: object[], currentValue: string, readOnly: boolean, showKey: boolean, classMarkKey: string, onRestore: (entry: object, changeId: number) => void}} props
 *   `entries` is the whole list of the language: `restoredFromId` is looked up in it (T50).
 *   `currentValue` is the key's value in `terms`; `''` = absent (inherited).
 *   `readOnly` (file not readable, RN25) never shows the restore button.
 *   `showKey` puts the key first ("Language history", every key together) with its class mark
 *   (`classMarkKey`, T12/T13) when it is not a label.
 *   `isLast` (V7, RN16c): the oldest entry of the list has a dot but no line going down to a next one.
 */
const HistoryEntry = ({ entry, entries = [], currentValue = '', readOnly = false, showKey = false, classMarkKey = '', isLast = false, onRestore = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const valueOf = (value) => (value === null ? t_('Inherited') : value)
  // RN16d: a restoration carries F's id; the state it went back to is the entry of the key just before F.
  const restoredFrom = entries.find((candidate) => candidate.id === entry.restoredFromId) || {}
  const ofRestoredKey = entries.filter((candidate) => candidate.key === restoredFrom.key)
  const restoredState = ofRestoredKey[ofRestoredKey.findIndex((candidate) => candidate.id === restoredFrom.id) + 1] || {}
  const restoredText = !('id' in restoredFrom)
    ? t_('Restored')
    : !('id' in restoredState) ? t_('Back to the original inherited value') : t_('Back to the state of {{date}}', { date: formatLocalDateTime(restoredState.changedAt) })
  const isOutsideMic = entry.origin === 'OUTSIDE_MIC'
  // RN16d (DC-21): the entry is a state; the button puts back its NEW value, sending F's id.
  const goBack = goBackTarget(entry, entries, currentValue, readOnly)

  // RN16c: the entry in force is the most recent one of its key (`entries` come most recent first, 2.4).
  // ...and only if its new value is the key's value now: otherwise (e.g. RN31) it does not claim what is not.
  const latest = entries.find((candidate) => candidate.key === entry.key) || {}
  const isCurrent = latest.id === entry.id && (entry.newValue === null ? '' : entry.newValue) === currentValue
  const testId = `history-entry-${entry.id}`
  const author = isOutsideMic ? t_('Edited outside MIC') : entry.author
  const when = isOutsideMic ? t_('Detected on {{date}}', { date: formatLocalDateTime(entry.changedAt) }) : formatLocalDateTime(entry.changedAt)

  return (
    <Box component='li' data-testid={testId} sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, listStyle: 'none' }}>
      {/* V7: the timeline — a dot per entry, joined to the next (older) one by a vertical line */}
      <Box aria-hidden sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1 }}>
        <Box data-testid='history-dot' sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: isCurrent ? 'primary.main' : 'text.secondary', flexShrink: 0 }} />
        {isLast ? null : <Box data-testid='history-connector' sx={{ width: 2, flexGrow: 1, minHeight: 24, bgcolor: 'divider' }} />}
      </Box>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pb: 1 }}>
        <Box>
          {showKey ? <Typography data-testid={`${testId}-key`} component='span' variant='caption'>{entry.key}</Typography> : null}
          {classMarkKey !== '' ? <Typography component='span' variant='caption'>{t_(classMarkKey)}</Typography> : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography data-testid={`${testId}-change`} component='span' variant='h6'>{`${valueOf(entry.previousValue)} → ${valueOf(entry.newValue)}`}</Typography>
            {isCurrent ? <Chip size='small' label={t_('Current')} /> : null}
          </Box>
          {/* RN20: no author when it was found outside MIC; its date is the moment of detection (T25, T26) */}
          <Typography data-testid={`${testId}-meta`} variant='body2' color='text.secondary'>{`${author} · ${when}`}</Typography>
          {entry.restoredFromId !== null
            ? <Typography data-testid={`${testId}-restored`} variant='body2' color='text.secondary'>{restoredText}</Typography>
            : null}
        </Box>
        {goBack.available
          ? <Button size='small' sx={{ minWidth: 40, minHeight: 40 }} onClick={() => onRestore(entry, goBack.changeId)}>{t_('Go back here')}</Button>
          : null}
      </Box>
    </Box>
  )
}

export default HistoryEntry
